import L from 'leaflet';
import 'leaflet-measure-path';
import 'leaflet-measure-path/leaflet-measure-path.css';
import { effectiveImperial } from '@iso/lib/helpers/mapFurniture';

/**
 * On-path measurements — segment lengths and enclosed area drawn onto every
 * polyline, polygon and circle.
 *
 * Enabled from one place like the rest of the map behaviour. Unlike
 * leaflet-rotate, mergeOptions is the right tool here: the plugin reads
 * `this.options.showMeasurements` in its onAdd override, which runs well after
 * Leaflet has merged the prototype defaults.
 *
 * L.Polygon and L.Rectangle inherit this, because Leaflet's class extend gives
 * a subclass an options object whose prototype IS the parent's — so a key added
 * to L.Polyline's options later is visible down the chain.
 *
 * Opt a layer out with `showMeasurements={false}`, or soften it per layer with
 * `measurementOptions={{ showOnHover: true }}`. Off by default across the app
 * until the property turns them on.
 */

const SQ_METRES_PER_HECTARE = 10000;
const SQ_METRES_PER_SQ_KM = 1000000;
const SQ_METRES_PER_ACRE = 4046.85642;
const SQ_METRES_PER_SQ_FOOT = 0.09290304;
const METRES_PER_FOOT = 0.3048;
const METRES_PER_MILE = 1609.344;

/** Two significant-ish figures: fine detail when small, no decimal noise when big. */
const round = (value) => (value < 100 ? value.toFixed(1) : Math.round(value));

/**
 * Read the unit system per call rather than capturing it, so that toggling the
 * scale bar relabels existing measurements on the next update. The plugin
 * copies its options object on show, so a captured flag could not be changed.
 */
const formatDistance = (metres) => {
  if (effectiveImperial(false)) {
    const feet = metres / METRES_PER_FOOT;
    return feet > 3000
      ? `${round(metres / METRES_PER_MILE)} mi`
      : `${round(feet)} ft`;
  }
  return metres > 1000 ? `${round(metres / 1000)} km` : `${round(metres)} m`;
};

/**
 * Hectares rather than the plugin's square metres for the mid range — a paddock
 * reads as "14.6 ha", not "146000 m²", and that is the unit the rest of the app
 * reports acreage in.
 */
const formatArea = (sqMetres) => {
  if (effectiveImperial(false)) {
    return sqMetres > SQ_METRES_PER_ACRE / 10
      ? `${round(sqMetres / SQ_METRES_PER_ACRE)} ac`
      : `${round(sqMetres / SQ_METRES_PER_SQ_FOOT)} ft²`;
  }
  if (sqMetres < SQ_METRES_PER_HECTARE) return `${round(sqMetres)} m²`;
  if (sqMetres < SQ_METRES_PER_SQ_KM * 10) {
    return `${round(sqMetres / SQ_METRES_PER_HECTARE)} ha`;
  }
  return `${round(sqMetres / SQ_METRES_PER_SQ_KM)} km²`;
};

const MEASUREMENT_OPTIONS = { formatDistance, formatArea };

/**
 * Per-edge distances are a property-level choice — some properties want the
 * segment lengths on every boundary, others find them noise. The flag lives
 * here rather than on each map because a user only ever looks at one property
 * at a time, and every map in the app should agree about it.
 *
 * Read per call in `updateMeasurements` (below) for the same reason the unit
 * system is: the plugin copies its options into each layer when the layer is
 * shown, so a flag captured at that moment could never be changed afterwards.
 *
 * Both halves move together: turning it off takes the enclosed area with the
 * edge lengths, so a property that wants a clean map gets one rather than a
 * paddock still carrying a number in the middle of it.
 *
 * Off until a property says otherwise. A paddock map is read for its shapes,
 * and a number on every edge of every boundary is noise on most of them — so
 * the labels are something a property turns ON, and a map that has not been
 * told anything yet (an older cached payload, a fetch still in flight) draws
 * clean rather than flashing a full set of labels and then dropping them.
 */
let measurementsEnabled = false;

// Open maps, so switching the flag can repaint all of them at once. Mirrors
// the scale bar's registry in mapFurniture.js.
const liveMeasurementMaps = new Set();

// Collect first, then update: an update adds and removes label layers on the
// map, and Leaflet's eachLayer walks the very collection that would change.
const refreshMeasurements = (map) => {
  const measured = [];

  map.eachLayer((layer) => {
    if (typeof layer.updateMeasurements === 'function') measured.push(layer);
  });

  measured.forEach((layer) => layer.updateMeasurements());
};

/**
 * Turn the on-path measurement labels — edge lengths and enclosed area — on or
 * off for every open map.
 *
 * Called when the active property changes, so the setting follows the property
 * onto every map that draws the property's shapes — the property map, the
 * shared public map, the small maps inside modals — without any of them
 * knowing about it. A map of something else, like the tracking trail on
 * /activity, opts out per layer instead and never sees this.
 *
 * Strictly `=== true`, so anything else means off. The column arrives from the
 * API as a number, and `0` is the value a caller is most likely to hand over
 * without converting — the one shape that must not read as "on". Callers do
 * convert (`Number(...) === 1`), and this keeps the one that forgets pointing
 * the same way as the default above.
 */
export const setMeasurementsEnabled = (enabled) => {
  const next = enabled === true;
  if (next === measurementsEnabled) return;

  measurementsEnabled = next;
  liveMeasurementMaps.forEach(refreshMeasurements);
};

/**
 * What the flag is right now, for a component that needs to open showing the
 * property's answer rather than the default.
 *
 * `changeActiveProperty` has usually already applied it by the time a map
 * mounts, so a component that starts from a guess and corrects itself after its
 * own fetch disagrees with the map beside it for as long as that fetch takes —
 * a visible flash, and a state that sticks if the fetch fails.
 */
export const getMeasurementsEnabled = () => measurementsEnabled;

/**
 * Teach the measurement labels about map rotation.
 *
 * leaflet-rotate positions a real L.Marker by converting the point into the
 * rotated pane's frame first (`_setPos` -> `rotatedPointToMapPanePoint`), and
 * adds the map bearing to any icon that should turn with the view. But
 * L.Marker.Measurement extends L.Layer directly rather than L.Marker, so it
 * gets neither: its labels were placed at unrotated coordinates and kept their
 * unrotated segment angle, so they drifted off their paths as soon as the map
 * was turned.
 *
 * Both are radians here — the plugin writes `rotate(<n>rad)` and leaflet-rotate
 * stores `_bearing` in radians.
 */
const applyRotation = (marker, point) => {
  const map = marker._map;
  L.DomUtil.setPosition(marker._element, map._rotate ? map.rotatedPointToMapPanePoint(point) : point);
  const angle = marker._rotation + (map._rotate ? map._bearing : 0);
  marker._element.style.transform += ` rotate(${angle}rad)`;
};

if (!L.Polyline.airagriMeasurementsRegistered) {
  L.Polyline.airagriMeasurementsRegistered = true;

  L.Marker.Measurement.include({
    _setPosition: function () {
      applyRotation(this, this._map.latLngToLayerPoint(this._latlng));
    },

    _animateZoom: function (opt) {
      applyRotation(this, this._map._latLngToNewLayerPoint(this._latlng, opt.zoom, opt.center).round());
    },
  });

  // The plugin only redraws labels on zoom, so without this they keep their
  // old screen positions while the map turns underneath them.
  L.Map.addInitHook(function () {
    this.on('rotate', () => refreshMeasurements(this));

    liveMeasurementMaps.add(this);
    this.on('unload', () => liveMeasurementMaps.delete(this));
  });

  /**
   * Rebuild the labels on every geometry change, not just the two the plugin
   * knows about.
   *
   * leaflet-measure-path only hooks `setLatLngs` and `spliceLatLngs`, and
   * Leaflet.draw uses neither while a shape is being drawn: `addVertex` grows
   * the in-progress shape with `addLatLng` and hands it to the map the moment
   * it has two points. That hand-off is an `onAdd`, so the first segment got a
   * label — and nothing ever refreshed it again. Every later edge stayed blank
   * for the rest of the draw. Editing had the same hole: dragging a vertex goes
   * through `redraw`, so the labels sat at their old positions and values.
   *
   * `redraw` is the funnel all of them share — `setLatLngs`, `addLatLng` and
   * Leaflet.draw's edit handlers all end there — and Leaflet does not call it
   * to pan or zoom (`_reset` projects and updates instead), so hooking it adds
   * nothing to ordinary map movement.
   *
   * Coalesced onto the next frame because a vertex drag fires `redraw` on every
   * mousemove, and each rebuild throws away and recreates a label element per
   * segment.
   *
   * Skipped entirely while the labels are off, which since the column defaults
   * to 0 is the ordinary case. There is nothing on screen to keep in step, so
   * the rebuild would only clear an already-empty layer group — once per
   * polyline per mousemove for the whole of a vertex drag. Nothing is lost by
   * missing those: `setMeasurementsEnabled` repaints every open map when it
   * flips, so a geometry change made while the labels were off is picked up
   * from the layer's current coordinates the moment they come back on.
   */
  const baseRedraw = L.Polyline.prototype.redraw;

  L.Polyline.include({
    redraw: function () {
      const result = baseRedraw.call(this);

      if (measurementsEnabled && this._measurementLayer && !this._measurementFrame) {
        this._measurementFrame = L.Util.requestAnimFrame(function () {
          this._measurementFrame = null;
          if (this._measurementLayer) this.updateMeasurements();
        }, this);
      }

      return result;
    },
  });

  /**
   * Honour the property's measurement choice at the moment the labels are
   * built, so switching it needs no layer teardown.
   *
   * A layer that asked to be left alone still wins: `showDistances: false` or
   * `showArea: false` in its own `measurementOptions` keeps that label off
   * whatever the property says.
   */
  const shows = (layer, key) => {
    const authored = layer.options.measurementOptions;

    return measurementsEnabled && !(authored && authored[key] === false);
  };

  const basePolylineUpdate = L.Polyline.prototype.updateMeasurements;

  L.Polyline.include({
    updateMeasurements: function () {
      if (this._measurementOptions) {
        this._measurementOptions.showDistances = shows(this, 'showDistances');
        this._measurementOptions.showArea = shows(this, 'showArea');
      }

      return basePolylineUpdate.call(this);
    },
  });

  // Circles carry an area and nothing else, and they are a separate class in
  // the plugin — a hazard radius has to go quiet with everything else.
  const baseCircleUpdate = L.Circle.prototype.updateMeasurements;

  L.Circle.include({
    updateMeasurements: function () {
      if (this._measurementOptions) {
        this._measurementOptions.showArea = shows(this, 'showArea');
      }

      return baseCircleUpdate.call(this);
    },
  });

  L.Polyline.mergeOptions({
    showMeasurements: true,
    measurementOptions: MEASUREMENT_OPTIONS,
  });

  L.Circle.mergeOptions({
    showMeasurements: true,
    measurementOptions: MEASUREMENT_OPTIONS,
  });
}
